import * as React from "react"
import { useLocation } from "react-router-dom"

import { NavPlatform } from "@/components/nav-platform"
import { NavExplore } from "@/components/nav-explore"
import { NavChats } from "@/components/nav-chats"
import { NavUser } from "@/components/nav-user"
import { TeamSwitcher } from "@/components/team-switcher"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/components/auth/AuthProvider"
import { useProfile } from "@/hooks/useProfile"
import { getUserDisplayName, getUserInitials } from "@/utils/user-profile"

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user, logout } = useAuth();
  const { profile } = useProfile();
  const location = useLocation();
  const { state } = useSidebar();

  // Enhanced avatar loading - prioritize MongoDB profile data, then auth metadata
  const getAvatarUrl = (user: any, profile: any) => {
    // PRIORITY 1: MongoDB profile data (most up-to-date)
    if (profile?.avatar_url) {
      return profile.avatar_url;
    }

    if (!user) {
      return undefined;
    }
    
    // PRIORITY 2: Auth user metadata - only check picture and avatar_url
    if (user.user_metadata?.picture) {
      return user.user_metadata.picture;
    }
    if (user.user_metadata?.avatar_url) {
      return user.user_metadata.avatar_url;
    }
    
    return undefined;
  };

  // Only create formatted user if user exists (no fallback needed)
  const formattedUser = user ? {
    name: getUserDisplayName(user),
    email: user.email || 'No email provided',
    avatar: getAvatarUrl(user, profile),
    avatarFallback: getUserInitials(user) || 'U'
  } : null;

  // Handle logout with immediate UI update
  const handleLogout = async () => {
    try {
      await logout();
      // The auth provider handles all localStorage cleanup
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Don't render sidebar if no user (this shouldn't happen in protected routes)
  if (!user || !formattedUser) {
    return null;
  }
  
  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher />
      </SidebarHeader>
      <SidebarContent className="gap-0">
        <NavPlatform />
        <NavExplore />
        {state === "expanded" && <NavChats />}
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={formattedUser} onLogout={handleLogout} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
